import { normalizeImageSource } from '@/utils/imageSource';
import {
  CameraOutlined,
  DeleteOutlined,
  EditOutlined,
  ReloadOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import {
  Alert,
  Avatar,
  Button,
  Modal,
  Select,
  Slider,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const { Text } = Typography;

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;
const CROP_VIEWPORT_SIZE = 240;
const OUTPUT_IMAGE_SIZE = 512;

const SUPPORTED_IMAGE_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

type CropOffset = {
  x: number;
  y: number;
};

type DragState = {
  pointerId: number;
  startX: number;
  startY: number;
  originX: number;
  originY: number;
};

type CurrentUserPhotoEditorProps = {
  imageUrl?: string | null;
  hasPendingChanges?: boolean;
  onChange: (nextImageDataUrl: string) => void;
  onDelete?: () => void;
  onReset?: () => void;
  disabled?: boolean;
  modalWidth?: number | string;
};

type CameraRequestOptions = {
  video: MediaTrackConstraints | boolean;
  audio: false;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const isDataUrl = (value: string) => value.startsWith('data:image/');

const toDataUrl = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
        return;
      }
      reject(new Error('No pudimos leer la imagen'));
    };
    reader.onerror = () => reject(new Error('No pudimos leer la imagen'));
    reader.readAsDataURL(blob);
  });

const loadImageFromSource = (source: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(new Error('No pudimos abrir la imagen seleccionada'));
    image.src = source;
  });

const resolveSourceForEditing = async (source: string): Promise<string> => {
  if (isDataUrl(source)) {
    return source;
  }

  const response = await fetch(source, { credentials: 'include' });
  if (!response.ok) {
    throw new Error('No pudimos descargar la imagen para editarla');
  }

  const blob = await response.blob();
  if (!blob.type.startsWith('image/')) {
    throw new Error('El archivo no es una imagen válida');
  }

  return toDataUrl(blob);
};

const waitForVideoReady = (video: HTMLVideoElement, timeoutMs = 4000) =>
  new Promise<void>((resolve, reject) => {
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      resolve();
      return;
    }

    let done = false;
    let timeoutId: number | undefined;
    let handleReady: () => void = () => {};
    let handleError: () => void = () => {};

    handleReady = () => {
      if (done) {
        return;
      }
      done = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('canplay', handleReady);
      video.removeEventListener('playing', handleReady);
      video.removeEventListener('error', handleError);
      resolve();
    };

    handleError = () => {
      if (done) {
        return;
      }
      done = true;
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('canplay', handleReady);
      video.removeEventListener('playing', handleReady);
      video.removeEventListener('error', handleError);
      reject(new Error('VIDEO_PREVIEW_ERROR'));
    };

    timeoutId = window.setTimeout(() => {
      if (done) {
        return;
      }
      done = true;
      video.removeEventListener('loadeddata', handleReady);
      video.removeEventListener('canplay', handleReady);
      video.removeEventListener('playing', handleReady);
      video.removeEventListener('error', handleError);
      reject(new Error('VIDEO_PREVIEW_TIMEOUT'));
    }, timeoutMs);

    video.addEventListener('loadeddata', handleReady);
    video.addEventListener('canplay', handleReady);
    video.addEventListener('playing', handleReady);
    video.addEventListener('error', handleError);
  });

const getCameraErrorMessage = (error: unknown) => {
  if (typeof window !== 'undefined' && !window.isSecureContext) {
    return 'Acceso a cámara requiere un contexto seguro (HTTPS o localhost).';
  }

  if (error instanceof Error && error.message === 'VIDEO_PREVIEW_TIMEOUT') {
    return 'La cámara respondió, pero no pudimos mostrar la vista previa. Probá otra cámara.';
  }
  if (error instanceof Error && error.message === 'VIDEO_PREVIEW_ERROR') {
    return 'No pudimos renderizar la vista previa de cámara. Probá nuevamente.';
  }
  if (error instanceof Error && error.message === 'VIDEO_ELEMENT_NOT_READY') {
    return 'No pudimos inicializar la vista previa de cámara. Probá nuevamente.';
  }

  const cameraError = error as DOMException | undefined;
  switch (cameraError?.name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return 'Permiso de cámara denegado. Revisá permisos del navegador y del sistema operativo.';
    case 'NotFoundError':
      return 'No encontramos una cámara disponible en este dispositivo.';
    case 'NotReadableError':
    case 'AbortError':
      return 'No pudimos iniciar la cámara. Cerrá otras apps que la estén usando e intentá nuevamente.';
    case 'OverconstrainedError':
      return 'La cámara seleccionada no está disponible. Probá con otra cámara.';
    default:
      return 'No pudimos acceder a la cámara. Verificá permisos e intentá nuevamente.';
  }
};

const CurrentUserPhotoEditor: React.FC<CurrentUserPhotoEditorProps> = ({
  imageUrl,
  hasPendingChanges,
  onChange,
  onDelete,
  onReset,
  disabled,
  modalWidth = 520,
}) => {
  const [isProcessingImage, setIsProcessingImage] = useState(false);
  const [isCameraModalOpen, setCameraModalOpen] = useState(false);
  const [isCameraStarting, setIsCameraStarting] = useState(false);
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [cameraError, setCameraError] = useState<string>();
  const [isCropModalOpen, setCropModalOpen] = useState(false);
  const [cropSource, setCropSource] = useState<string | null>(null);
  const [cropImageSize, setCropImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<CropOffset>({ x: 0, y: 0 });
  const [previewImageError, setPreviewImageError] = useState(false);
  const [availableCameras, setAvailableCameras] = useState<MediaDeviceInfo[]>(
    [],
  );
  const [selectedCameraId, setSelectedCameraId] = useState<
    string | undefined
  >();
  const normalizedImageUrl = useMemo(
    () => normalizeImageSource(imageUrl),
    [imageUrl],
  );
  const previewImageUrl = previewImageError ? undefined : normalizedImageUrl;

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const dragStateRef = useRef<DragState | null>(null);

  const isCameraSupported =
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices?.getUserMedia);

  const baseScale = useMemo(() => {
    if (!cropImageSize) {
      return 1;
    }
    return Math.max(
      CROP_VIEWPORT_SIZE / cropImageSize.width,
      CROP_VIEWPORT_SIZE / cropImageSize.height,
    );
  }, [cropImageSize]);

  const totalScale = baseScale * zoom;

  const clampOffset = useCallback(
    (nextOffset: CropOffset): CropOffset => {
      if (!cropImageSize) {
        return { x: 0, y: 0 };
      }
      const renderedWidth = cropImageSize.width * totalScale;
      const renderedHeight = cropImageSize.height * totalScale;
      const maxX = Math.max(0, (renderedWidth - CROP_VIEWPORT_SIZE) / 2);
      const maxY = Math.max(0, (renderedHeight - CROP_VIEWPORT_SIZE) / 2);

      return {
        x: clamp(nextOffset.x, -maxX, maxX),
        y: clamp(nextOffset.y, -maxY, maxY),
      };
    },
    [cropImageSize, totalScale],
  );

  useEffect(() => {
    setOffset((currentOffset) => clampOffset(currentOffset));
  }, [clampOffset]);

  useEffect(() => {
    setPreviewImageError(false);
  }, [normalizedImageUrl]);

  const stopCameraStream = useCallback(() => {
    if (!streamRef.current) {
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
      return;
    }
    streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const closeCameraModal = useCallback(() => {
    stopCameraStream();
    setCameraModalOpen(false);
    setIsCameraStarting(false);
    setIsVideoReady(false);
    setCameraError(undefined);
    setAvailableCameras([]);
    setSelectedCameraId(undefined);
  }, [stopCameraStream]);

  useEffect(() => {
    if (!isCameraModalOpen) {
      return;
    }

    if (!isCameraSupported) {
      setCameraError('Tu navegador no soporta acceso a cámara');
      return;
    }

    let cancelled = false;

    const loadVideoDevices = async () => {
      if (!navigator.mediaDevices?.enumerateDevices) {
        return [] as MediaDeviceInfo[];
      }
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        return devices.filter((device) => device.kind === 'videoinput');
      } catch (_) {
        // A stream can still work even if device enumeration fails.
        return [] as MediaDeviceInfo[];
      }
    };

    const startCamera = async () => {
      setIsCameraStarting(true);
      setIsVideoReady(false);
      setCameraError(undefined);
      stopCameraStream();

      try {
        const attempts: CameraRequestOptions[] = selectedCameraId
          ? [
              {
                video: { deviceId: { exact: selectedCameraId } },
                audio: false,
              },
              { video: true, audio: false },
            ]
          : [
              { video: true, audio: false },
              { video: { facingMode: { ideal: 'user' } }, audio: false },
            ];

        let stream: MediaStream | null = null;
        let lastError: unknown;

        for (const attempt of attempts) {
          try {
            stream = await navigator.mediaDevices.getUserMedia(attempt);
            break;
          } catch (error) {
            lastError = error;
          }
        }

        if (!stream) {
          throw lastError;
        }

        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;
        const videoTrack = stream.getVideoTracks()[0];
        if (!videoTrack || videoTrack.readyState !== 'live') {
          throw new DOMException(
            'No se encontró un track de video activo',
            'NotReadableError',
          );
        }

        const videoElement = videoRef.current;
        if (!videoElement) {
          throw new Error('VIDEO_ELEMENT_NOT_READY');
        }

        videoElement.srcObject = stream;
        videoElement.muted = true;
        videoElement.playsInline = true;
        const playPromise = videoElement.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => undefined);
        }

        await waitForVideoReady(videoElement);

        const retryPlayPromise = videoElement.play();
        if (retryPlayPromise && typeof retryPlayPromise.catch === 'function') {
          retryPlayPromise.catch(() => undefined);
        }
        if (!cancelled) {
          setIsVideoReady(true);
          setIsCameraStarting(false);
        }

        const videoDevices = await loadVideoDevices();
        if (!cancelled) {
          setAvailableCameras(videoDevices);
        }
      } catch (error) {
        if (!cancelled) {
          setIsCameraStarting(false);
          setIsVideoReady(false);
          setCameraError(getCameraErrorMessage(error));
        }
      } finally {
        if (!cancelled) {
          setIsCameraStarting(false);
        }
      }
    };

    startCamera();

    return () => {
      cancelled = true;
      stopCameraStream();
    };
  }, [
    isCameraModalOpen,
    isCameraSupported,
    selectedCameraId,
    stopCameraStream,
  ]);

  const openCropEditor = useCallback(async (source: string) => {
    setIsProcessingImage(true);
    try {
      const editableSource = await resolveSourceForEditing(source);
      const image = await loadImageFromSource(editableSource);
      setCropSource(editableSource);
      setCropImageSize({
        width: image.naturalWidth || image.width,
        height: image.naturalHeight || image.height,
      });
      setZoom(1);
      setOffset({ x: 0, y: 0 });
      setCropModalOpen(true);
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : 'No pudimos preparar la imagen para editar',
      );
    } finally {
      setIsProcessingImage(false);
    }
  }, []);

  const handleBeforeUpload = useCallback<
    NonNullable<UploadProps['beforeUpload']>
  >(
    async (file) => {
      if (!SUPPORTED_IMAGE_TYPES.has(file.type)) {
        message.error('Formato inválido. Usá JPG, PNG o WEBP');
        return Upload.LIST_IGNORE;
      }
      if (file.size > MAX_IMAGE_SIZE_BYTES) {
        message.error('La imagen supera 5 MB');
        return Upload.LIST_IGNORE;
      }

      try {
        const dataUrl = await toDataUrl(file as Blob);
        await openCropEditor(dataUrl);
      } catch (_) {
        message.error('No pudimos leer la imagen seleccionada');
      }

      return Upload.LIST_IGNORE;
    },
    [openCropEditor],
  );

  const clearDragState = useCallback(() => {
    dragStateRef.current = null;
  }, []);

  const handleCropPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (!cropImageSize) {
        return;
      }
      event.preventDefault();
      dragStateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: offset.x,
        originY: offset.y,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [cropImageSize, offset.x, offset.y],
  );

  const handleCropPointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;

      setOffset(
        clampOffset({
          x: dragState.originX + deltaX,
          y: dragState.originY + deltaY,
        }),
      );
    },
    [clampOffset],
  );

  const handleCropPointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const dragState = dragStateRef.current;
      if (!dragState || dragState.pointerId !== event.pointerId) {
        return;
      }
      clearDragState();
      event.currentTarget.releasePointerCapture(event.pointerId);
    },
    [clearDragState],
  );

  const closeCropModal = useCallback(() => {
    setCropModalOpen(false);
    setCropSource(null);
    setCropImageSize(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    clearDragState();
  }, [clearDragState]);

  const handleEditCurrentPhoto = useCallback(async () => {
    if (!previewImageUrl) {
      return;
    }
    await openCropEditor(previewImageUrl);
  }, [openCropEditor, previewImageUrl]);

  const handleCaptureFromCamera = useCallback(async () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    const width = video.videoWidth;
    const height = video.videoHeight;
    if (!width || !height) {
      message.error('No pudimos capturar la imagen');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      message.error('No pudimos capturar la imagen');
      return;
    }
    context.drawImage(video, 0, 0, width, height);
    const capturedDataUrl = canvas.toDataURL('image/jpeg', 0.92);

    closeCameraModal();
    await openCropEditor(capturedDataUrl);
  }, [closeCameraModal, openCropEditor]);

  const handleApplyCrop = useCallback(async () => {
    if (!cropSource || !cropImageSize) {
      return;
    }

    setIsProcessingImage(true);
    try {
      const image = await loadImageFromSource(cropSource);
      const canvas = document.createElement('canvas');
      canvas.width = OUTPUT_IMAGE_SIZE;
      canvas.height = OUTPUT_IMAGE_SIZE;
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('No pudimos procesar la imagen');
      }

      const sourceSize = CROP_VIEWPORT_SIZE / totalScale;
      const sourceX =
        cropImageSize.width / 2 -
        (CROP_VIEWPORT_SIZE / 2 + offset.x) / totalScale;
      const sourceY =
        cropImageSize.height / 2 -
        (CROP_VIEWPORT_SIZE / 2 + offset.y) / totalScale;
      const safeSourceX = clamp(
        sourceX,
        0,
        Math.max(0, cropImageSize.width - sourceSize),
      );
      const safeSourceY = clamp(
        sourceY,
        0,
        Math.max(0, cropImageSize.height - sourceSize),
      );

      context.drawImage(
        image,
        safeSourceX,
        safeSourceY,
        sourceSize,
        sourceSize,
        0,
        0,
        OUTPUT_IMAGE_SIZE,
        OUTPUT_IMAGE_SIZE,
      );

      const nextImageDataUrl = canvas.toDataURL('image/jpeg', 0.9);
      onChange(nextImageDataUrl);
      closeCropModal();
    } catch (error) {
      message.error(
        error instanceof Error
          ? error.message
          : 'No pudimos aplicar la edición',
      );
    } finally {
      setIsProcessingImage(false);
    }
  }, [
    closeCropModal,
    cropImageSize,
    cropSource,
    offset.x,
    offset.y,
    onChange,
    totalScale,
  ]);

  const canEditCurrentPhoto = Boolean(previewImageUrl);

  return (
    <>
      <Space
        direction="vertical"
        size={12}
        style={{
          width: '100%',
          alignItems: 'center',
        }}
      >
        <Avatar
          size={108}
          src={previewImageUrl || undefined}
          icon={<UserOutlined />}
          onError={() => {
            setPreviewImageError(true);
            return false;
          }}
        />
        <Text
          type="secondary"
          style={{ display: 'block', textAlign: 'center' }}
        >
          JPG, PNG o WEBP.
          <br />
          Máximo 5 MB.
        </Text>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Upload
            accept="image/jpeg,image/png,image/webp"
            showUploadList={false}
            beforeUpload={handleBeforeUpload}
            disabled={disabled || isProcessingImage}
            style={{ width: '100%' }}
          >
            <Button
              block
              icon={<UploadOutlined />}
              loading={isProcessingImage}
              disabled={disabled}
            >
              Subir
            </Button>
          </Upload>
          <Button
            block
            icon={<CameraOutlined />}
            onClick={() => setCameraModalOpen(true)}
            disabled={disabled || !isCameraSupported}
          >
            Tomar
          </Button>
          <Button
            block
            icon={<EditOutlined />}
            onClick={handleEditCurrentPhoto}
            disabled={disabled || !canEditCurrentPhoto}
          >
            Editar
          </Button>
          {onDelete && (
            <Button
              block
              icon={<DeleteOutlined />}
              onClick={onDelete}
              disabled={disabled || !canEditCurrentPhoto}
            >
              Eliminar
            </Button>
          )}
          {onReset && hasPendingChanges && (
            <Button
              block
              icon={<ReloadOutlined />}
              onClick={onReset}
              disabled={disabled}
            >
              Restablecer
            </Button>
          )}
        </Space>
      </Space>

      <Modal
        centered
        destroyOnHidden
        title="Tomar foto"
        open={isCameraModalOpen}
        width={modalWidth}
        onCancel={closeCameraModal}
        okText="Capturar"
        cancelText="Cancelar"
        onOk={handleCaptureFromCamera}
        okButtonProps={{
          disabled: Boolean(cameraError) || isCameraStarting || !isVideoReady,
        }}
      >
        {cameraError && (
          <Alert
            type="error"
            showIcon
            message={cameraError}
            style={{ marginBottom: 12 }}
          />
        )}
        {availableCameras.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong style={{ display: 'block', marginBottom: 6 }}>
              Cámara
            </Text>
            <Select
              value={selectedCameraId}
              onChange={(nextValue) => setSelectedCameraId(nextValue)}
              placeholder="Usar cámara predeterminada"
              allowClear
              style={{ width: '100%' }}
              options={availableCameras.map((camera, index) => ({
                value: camera.deviceId,
                label: camera.label || `Cámara ${index + 1}`,
              }))}
            />
          </div>
        )}
        <div
          style={{
            width: '100%',
            aspectRatio: '4 / 3',
            borderRadius: 8,
            overflow: 'hidden',
            background: '#f5f5f5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              visibility: cameraError ? 'hidden' : 'visible',
            }}
          />
          {isCameraStarting && !cameraError && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(245, 245, 245, 0.75)',
              }}
            >
              <Spin />
            </div>
          )}
        </div>
      </Modal>

      <Modal
        centered
        destroyOnHidden
        title="Editar foto"
        open={isCropModalOpen}
        width={modalWidth}
        onCancel={closeCropModal}
        onOk={handleApplyCrop}
        okText="Aplicar"
        cancelText="Cancelar"
        confirmLoading={isProcessingImage}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div
              role="presentation"
              onPointerDown={handleCropPointerDown}
              onPointerMove={handleCropPointerMove}
              onPointerUp={handleCropPointerUp}
              onPointerCancel={clearDragState}
              style={{
                width: CROP_VIEWPORT_SIZE,
                height: CROP_VIEWPORT_SIZE,
                borderRadius: '50%',
                border: '1px solid #d9d9d9',
                background: '#fafafa',
                overflow: 'hidden',
                position: 'relative',
                touchAction: 'none',
                cursor: 'grab',
              }}
            >
              {cropSource && cropImageSize && (
                <img
                  src={cropSource}
                  alt="Edición de foto de perfil"
                  draggable={false}
                  style={{
                    position: 'absolute',
                    left: '50%',
                    top: '50%',
                    width: cropImageSize.width,
                    height: cropImageSize.height,
                    transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${totalScale})`,
                    transformOrigin: 'center center',
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
              )}
            </div>
          </div>

          <div>
            <Text strong>Zoom</Text>
            <Slider
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setZoom(nextValue);
              }}
            />
          </div>
        </Space>
      </Modal>
    </>
  );
};

export default CurrentUserPhotoEditor;
